import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CdkDrag, CdkDragDrop, CdkDropList } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { NotificationService } from '../../services/notification.service';

/**
 * Whether the file's own first bytes say it is a JPEG, PNG or WebP. The type the
 * browser reports comes from the file's name and is empty or wrong for some files
 * (no extension, odd extension), so it can't be trusted. The server checks again.
 */
export async function isSupportedImage(file: File): Promise<boolean> {
  const b = new Uint8Array(await file.slice(0, 12).arrayBuffer());
  const text = (from: number, to: number) => String.fromCharCode(...b.slice(from, to));
  const jpeg = b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff;
  const png = b[0] === 0x89 && text(1, 4) === 'PNG';
  const webp = text(0, 4) === 'RIFF' && text(8, 12) === 'WEBP';
  return jpeg || png || webp;
}
export const MAX_FILE_MB = 15; // as on the server, which shrinks what it keeps
const MAX_FILE_BYTES = MAX_FILE_MB * 1024 * 1024;

/**
 * A grid of images with add / remove and, when `reorderable`, drag-and-drop
 * (or the arrow buttons) to put them in order. The first one is the cover.
 *
 * It only shows what it is given and reports what the user did; the parent
 * decides what that means (call the API, keep files until a form is saved...).
 */
@Component({
  selector: 'app-image-manager',
  standalone: true,
  imports: [CdkDropList, CdkDrag, MatButtonModule, MatIconModule, TranslocoPipe],
  templateUrl: './image-manager.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './image-manager.component.less',
})
export class ImageManagerComponent {
  /** Image URLs, in order. */
  @Input() set images(value: string[] | null | undefined) {
    this.items = [...(value ?? [])];
  }
  /** How many images there can be in all. */
  @Input() max = 10;
  /** Whether the order can be changed and the first image is shown as the cover. */
  @Input() reorderable = false;
  /** Disables every action, e.g. while a request is running. */
  @Input() busy = false;

  /** New files the user picked, already checked for type, size and how many fit. */
  @Output() upload = new EventEmitter<File[]>();
  @Output() remove = new EventEmitter<string>();
  /** The URLs in their new order. */
  @Output() reorder = new EventEmitter<string[]>();

  private notifications = inject(NotificationService);
  private transloco = inject(TranslocoService);

  items: string[] = [];

  async onPick(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const picked = Array.from(input.files ?? []);
    input.value = ''; // so picking the same file again still fires

    // Say which files were left out and why, they would otherwise just not appear.
    // One message: a snackbar replaces the one before it.
    const problems: string[] = [];
    const valid: File[] = [];
    for (const file of picked) {
      if (!(await isSupportedImage(file))) {
        problems.push(this.transloco.translate('imageManager.notAnImage', { name: file.name }));
      } else if (file.size > MAX_FILE_BYTES) {
        problems.push(
          this.transloco.translate('imageManager.tooLarge', { name: file.name, mb: MAX_FILE_MB }),
        );
      } else {
        valid.push(file);
      }
    }

    const accepted = valid.slice(0, Math.max(this.max - this.items.length, 0));
    if (accepted.length < valid.length) {
      problems.push(this.transloco.translate('imageManager.tooMany', { max: this.max }));
    }
    if (problems.length) this.notifications.error(problems.join(' '));
    if (accepted.length) this.upload.emit(accepted);
  }

  onDrop(event: CdkDragDrop<string[]>): void {
    this.move(event.previousIndex, event.currentIndex);
  }

  move(from: number, to: number): void {
    if (this.busy || to < 0 || to >= this.items.length || from === to) return;
    const next = [...this.items];
    next.splice(to, 0, ...next.splice(from, 1));
    this.items = next; // shown at once, the parent confirms or puts it back
    this.reorder.emit(next);
  }
}
